import { useViewTemplateById } from '@/hooks/useViewTemplateById';
import { Button } from '@heidi/design-system/button';
import {
  TypographyCaption,
  TypographyP2Bold,
  TypographyPBold,
} from '@heidi/design-system/typography';
import { useMutateDeleteTemplate } from '@/hooks/mutation/useMutateDeleteTemplate';
import { invalidateAllTemplatesQuery } from '@heidi/templates/data';
import { useInvokeOnce } from '@heidi/utils';
import { cn } from '@/lib/cn';
import { AlertCircle, ChevronRight, FileText, Loader2, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { TemplatePreviewThumbnail } from './TemplatePreviewThumbnail';

interface CreateTemplateBlockProps {
  status: 'applying' | 'completed' | 'error';
  templateId?: string;
  templateName?: string;
  error?: string;
  onBlockError: (blockType: 'create_template', error?: string) => void;
  className?: string;
}

/**
 * CreateTemplateBlock. Renders the status of an Ask Heidi `create_template`
 * tool call. On success, exposes a View action that deep-links into the
 * template editor and a Delete action that removes the freshly created
 * template via the standard delete mutation. After deletion, the card
 * switches to a tombstone state so the user can't trigger the delete twice.
 */
export const CreateTemplateBlock = ({
  status,
  templateId,
  templateName,
  error,
  onBlockError,
  className,
}: CreateTemplateBlockProps): JSX.Element => {
  const intl = useIntl();
  useInvokeOnce(() => onBlockError('create_template', error), status === 'error');
  const viewTemplate = useViewTemplateById();
  const { mutate: deleteTemplate, isPending: isDeleting } =
    useMutateDeleteTemplate();
  const [isDeleted, setIsDeleted] = useState(false);
  const didInvalidateListsRef = useRef(false);

  useEffect(() => {
    if (status !== 'completed' || !templateId || didInvalidateListsRef.current) {
      return;
    }
    didInvalidateListsRef.current = true;
    // Agent create_template bypasses the REST mutation hooks that normally
    // invalidate the library lists — refresh them so search/My Templates
    // pick up the new template immediately.
    void invalidateAllTemplatesQuery();
  }, [status, templateId]);

  if (status === 'applying') {
    return (
      <div
        className={cn(
          'flex w-full items-center gap-3 rounded-xl border border-border bg-surface-accent p-4',
          'transition-all duration-200',
          className
        )}
        data-testid="create-template-block"
        data-status="applying"
      >
        <FileText
          className="size-5 shrink-0 text-text-tertiary"
          aria-hidden="true"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyPBold className="line-clamp-2 text-text-primary">
            {templateName ?? (
              <FormattedMessage defaultMessage="New template" id="lGJaj2v+Fy" />
            )}
          </TypographyPBold>
          <div className="flex items-center gap-1.5">
            <Loader2 className="size-3 animate-spin text-text-tertiary" />
            <TypographyCaption className="text-text-tertiary">
              <FormattedMessage
                defaultMessage="Creating template..."
                id="BlgSepGLRd"
              />
            </TypographyCaption>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div
        className={cn(
          'flex w-full items-center gap-3 rounded-xl border border-validation-destructive-border bg-validation-destructive-muted p-4',
          'transition-all duration-200',
          className
        )}
        data-testid="create-template-block"
        data-status="error"
      >
        <AlertCircle
          className="size-5 shrink-0 text-validation-destructive"
          aria-hidden="true"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyPBold className="line-clamp-2 text-text-primary">
            <FormattedMessage
              defaultMessage="Couldn't create template"
              id="RUDydee+FP"
            />
          </TypographyPBold>
          <TypographyCaption className="text-validation-destructive">
            {error ?? (
              <FormattedMessage
                defaultMessage="Something went wrong while creating the template."
                id="U7aFSb3uOa"
              />
            )}
          </TypographyCaption>
        </div>
      </div>
    );
  }

  const resolvedName =
    templateName ??
    intl.formatMessage({
      defaultMessage: 'Template',
      id: '3JxaKsUDfq',
    });

  if (isDeleted) {
    return (
      <div
        className={cn(
          'flex w-full items-center gap-8 overflow-hidden rounded-xl border border-border bg-surface-accent p-4',
          'transition-all duration-200',
          className
        )}
        data-testid="create-template-block"
        data-status="deleted"
      >
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <TypographyPBold className="line-clamp-2 text-text-disabled line-through">
            <FormattedMessage
              defaultMessage="{name} created"
              id="uI5hko0GEN"
              values={{ name: resolvedName }}
            />
          </TypographyPBold>
          <TypographyP2Bold className="text-text-secondary">
            <FormattedMessage defaultMessage="Deleted" id="KQvWvDRI1B" />
          </TypographyP2Bold>
        </div>
        <div className="relative w-[104px] shrink-0 self-stretch">
          <TemplatePreviewThumbnail
            variant="deleted"
            className="absolute start-0 top-0"
          />
        </div>
      </div>
    );
  }

  // Completed
  const canView = Boolean(templateId);
  const canDelete = Boolean(templateId) && !isDeleting;

  const handleView = () => {
    if (!templateId) return;
    void viewTemplate(templateId);
  };

  const handleDelete = () => {
    if (!templateId) return;
    deleteTemplate(templateId);
    setIsDeleted(true);
  };

  return (
    <div
      className={cn(
        'flex w-full items-center gap-8 overflow-hidden rounded-xl border border-border bg-surface-accent p-4',
        'transition-all duration-200',
        className
      )}
      data-testid="create-template-block"
      data-status="completed"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <TypographyPBold className="line-clamp-2 text-text-primary">
          <FormattedMessage
            defaultMessage="{name} created"
            id="uI5hko0GEN"
            values={{ name: resolvedName }}
          />
        </TypographyPBold>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            flush="all"
            className="text-text-secondary"
            onClick={handleView}
            disabled={!canView}
          >
            <FormattedMessage defaultMessage="View" id="FgydNeoT78" />
            <ChevronRight className="rtl:-scale-x-100" />
          </Button>
          <Button
            variant="destructive_ghost"
            size="sm"
            flush="all"
            onClick={handleDelete}
            disabled={!canDelete}
          >
            <FormattedMessage defaultMessage="Delete" id="K3r6DQW7h+" />
            <Trash2 />
          </Button>
        </div>
      </div>
      <div className="relative w-[104px] shrink-0 self-stretch">
        <TemplatePreviewThumbnail className="absolute start-0 top-0" />
      </div>
    </div>
  );
};
